import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import DirectoryCategory from './DirectoryCategory';
import CompanyProfile from './CompanyProfile';

/**
 * Smart Router Component
 * Determines whether to show a category page or a company profile page
 * by checking if the second URL segment is a category slug or a company slug
 */
export default function DirectoryCategoryOrCompany() {
  const { familySlug, categorySlug } = useParams();
  const { API } = useContext(AppContext);
  const [routeType, setRouteType] = useState(null); // 'category' | 'company' | 'loading'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const determineRouteType = async () => {
      try {
        setLoading(true);
        
        // First, check if this is a valid family/category combination
        const categoryResponse = await axios.get(
          `${API}/directory/families/${familySlug}/${categorySlug}`
        );
        
        // If we get a valid response with professionals array, it's a category page
        if (categoryResponse.data && categoryResponse.data.name_fr) {
          setRouteType('category');
          return;
        }
      } catch (err) {
        // If category lookup fails, try as company page
        // familySlug becomes categorySlug, categorySlug becomes companySlug
        try {
          const companyResponse = await axios.get(
            `${API}/directory/company/${familySlug}/${categorySlug}`
          );
          
          if (companyResponse.data && companyResponse.data.company_name) {
            setRouteType('company');
            return;
          }
        } catch (companyErr) {
          // Neither found - default to category (will show not found)
          setRouteType('category');
        }
      } finally {
        setLoading(false);
      }
    };

    determineRouteType();
  }, [API, familySlug, categorySlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF]"></div>
      </div>
    );
  }

  if (routeType === 'company') {
    // Render company profile with familySlug as category and categorySlug as company
    return <CompanyProfile categorySlug={familySlug} companySlug={categorySlug} />;
  }

  // Default: render category page
  return <DirectoryCategory />;
}
